import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { Box, Heading } from "@chakra-ui/react";
import CustomBox from "../../components/Box";
import Button from "../../components/Button";
import Input from "../../components/Input";
import Modal from "../../components/Modal";
import { SchemaColumn } from "../../api/types";
import useGetSchema from "../../api/useGetSchema";
import useEntitySelection from "../../hooks/useEntitySelection";
import useFilter from "../../hooks/useFilter";
import { colors, sizes } from "../../settings/theme";
import { copyToClipboard } from "../../utils/copyToClipboard";
import { typedSxMap } from "../../utils/typedSxMap";
import SchemaColumnDelete from "./components/schemaColumnDelete";
import SchemaColumnForm from "./components/schemaColumnForm";
import SchemaColumnsTable from "./components/schemaColumnsTable";
import { PiCopyBold, PiCubeBold, PiMagnifyingGlass } from "react-icons/pi";


const styles = typedSxMap({
  mainText: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    "& h2": {
      marginBottom: 0,
      fontWeight: 500,
    },
  },
  headingWrapper: {
    marginBottom: "1rem",
  },
  supportingText: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    gap: "4px",
    color: "gray.400",
    "& p": {
      marginBottom: 0,
    },
  },
  title: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  inputWrapper: {
    backgroundColor: "var(--color-input-background)",
    borderRadius: "var(--border-radius-2)",
    outline: "1px solid transparent",
    padding: "calc(var(--m-xxs) / 2)",
    border: "1px solid var(--color-border)",
    fontWeight: 400,
    display: "flex",
    alignItems: "center",
    minWidth: "270px",
    position: "relative",
  },
  inputIcon: {
    padding: "0 var(--m-xxxs)",
    cursor: "pointer",
    display: "flex",
    position: "relative",
  },
  info: {},
  textsPanel: {},
  footer: {},
  table: {
    display: "table",
    overflow: "auto",
  },
  tbody: {
    display: "table-row-group",
  },
  tr: {
    display: "table-row",
  },
  td: {
    display: "table-cell",
    verticalAlign: "middle",
    maxWidth: "300px",
    height: "48px",
  },
  tableName: {
    display: "flex",
    alignItems: "center",
    gap: "var(--m-xs)",
  },
  importerIcon: {
    color: "var(--color-primary)",
  },

  tableWrapper: {
    display: "flex",
    overflowY: "auto",
    padding: "1px",
    marginRight: "-20px",
    paddingRight: "21px",
    maxHeight: "calc(100vh - 168px)",
    height: "auto",
    width: "100%",
  },
  actions: {
    display: "flex",
    gap: "var(--m-s)",
    alignItems: "center",
    "& .searchInput": {
      marginBottom: 0,
    },
  },
});

export default function Schema() {
  const { schemaId } = useParams();

  const { data: schema, isLoading } = useGetSchema(schemaId && schemaId !== "new" ? schemaId : "");

  const { entityId, action, update, modal } = useEntitySelection();

  const [columns, setColumns] = useState(schema?.schema_columns || []);

  const column = columns?.find((column) => column?.id === entityId);

  const tableColumns = columns?.filter((column) => column?.data_type === "table");

  useEffect(() => {
    if (schema?.schema_columns) {
      setColumns(schema.schema_columns);
    }
  }, [schema]);

  const modalContent = useMemo(
    () =>
      !entityId ? (
        <CustomBox>
          <SchemaColumnForm
            title="Add Column"
            buttonLabel="Save"
            existingTableColumns={tableColumns}
            onSuccess={modal.handleClose}
            column={column}
            context={{ schemaId: schema?.id }}
          />
        </CustomBox>
      ) : action === "edit" ? (
        <CustomBox>
          <SchemaColumnForm
            title="Edit Column"
            buttonLabel="Save"
            existingTableColumns={tableColumns}
            onSuccess={modal.handleClose}
            column={column}
            context={{ schemaId: schema?.id }}
          />
        </CustomBox>
      ) : action === "delete" ? (
        <CustomBox>
          <SchemaColumnDelete column={column} onSuccess={modal.handleClose} context={{ schemaId: schema?.id }} />{" "}
        </CustomBox>
      ) : null,
    [modal.openDelayed]
  );
  const dataWithCombinedProperty = (columns || []).map((item) => ({
    ...item,
    schemaColumnNameAndId: `${item.name}_${item.id}_${item.key}_${item.description}`,
  }));

  const { dataFiltered, setFilter } = useFilter<SchemaColumn[]>(["schemaColumnNameAndId"], dataWithCombinedProperty || []);

  if (isLoading) return null;
  return (
    <Box>
      <Box sx={styles.headingWrapper}>
        <Box sx={styles.title}>
          <Box sx={styles.mainText}>
            <Heading as="h1" size="m">
              {schema?.name}
            </Heading>
          </Box>
          <Box sx={styles.actions}>
            <Input
              icon={<PiMagnifyingGlass />}
              type="search"
              autoComplete="off"
              className="searchInput"
              placeholder="Search"
              onChange={(e: any) => setFilter(e.target.value)}
            />
            <Button variants={["primary"]} tabIndex={-1} onClick={() => modal.setOpen(true)}>
              Add Column
            </Button>
          </Box>
        </Box>
        <Box sx={styles.supportingText}>
          <p>{schema?.id}</p>
          <Button type="button" variants={["bare", "square"]} onClick={() => copyToClipboard(schema?.id || "")} title="Copy to clipboard">
            <PiCopyBold size={20} color={colors.textSoft} />
          </Button>
        </Box>
      </Box>

      <Box sx={styles.tableWrapper}>
        {schema && <SchemaColumnsTable schema={schema} update={update} dataFiltered={dataFiltered} setColumns={setColumns} />}
      </Box>
      {modal.openDelayed && (
        <Modal {...modal} useBox={false} useCloseButton>
          {modalContent}
        </Modal>
      )}
    </Box>
  );
}